/**
 * Outils de la Boîte à outils reliés à une SKILLCARD (table `card_tools`,
 * migration 0551, lien N-N). Lus et écrits par l'auteur de la carte ou un
 * admin (RLS de `cards`) ; tout le monde les lit via `get_public_card`.
 *
 * Lien VIVANT : pas besoin de republier la carte, la page publique et le
 * player voient le changement tout de suite.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { publicCardQueryKey } from './api';

// table postérieure à types.generated.ts (stale volontaire, cf. CLAUDE.md)
const db = supabase as unknown as SupabaseClient;

export const cardToolsQueryKey = (cardId: string) => ['card-tools', cardId] as const;

/** Identifiants des outils reliés, dans l'ordre choisi par l'auteur. */
export function useCardTools(cardId: string) {
  return useQuery({
    queryKey: cardToolsQueryKey(cardId),
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await db
        .from('card_tools')
        .select('tool_id, position')
        .eq('card_id', cardId)
        .order('position')
        .order('tool_id');
      if (error) throw new Error(error.message);
      return ((data ?? []) as { tool_id: string }[]).map((r) => r.tool_id);
    },
    enabled: !!cardId,
  });
}

/**
 * Remplace les outils reliés par la liste donnée (ordre = position). On
 * écrit d'abord la nouvelle liste (upsert), puis on retire les autres : un
 * échec à mi-chemin laisse un outil de trop, jamais une carte vidée.
 */
export function useSetCardTools(cardId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (toolIds: readonly string[]) => {
      const unique = [...new Set(toolIds)];
      if (unique.length > 0) {
        const up = await db.from('card_tools').upsert(
          unique.map((tool_id, position) => ({ card_id: cardId, tool_id, position })),
          { onConflict: 'card_id,tool_id' },
        );
        if (up.error) throw new Error(up.error.message);
      }
      let del = db.from('card_tools').delete().eq('card_id', cardId);
      if (unique.length > 0) del = del.not('tool_id', 'in', `(${unique.join(',')})`);
      const res = await del;
      if (res.error) throw new Error(res.error.message);
      return unique;
    },
    onMutate: (toolIds) => {
      // la liste bouge tout de suite sous le doigt de l'auteur
      qc.setQueryData(cardToolsQueryKey(cardId), [...new Set(toolIds)]);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: cardToolsQueryKey(cardId) });
      void qc.invalidateQueries({ queryKey: publicCardQueryKey(cardId) });
    },
  });
}
